import { API_BASE_URL, log, API_TIMEOUT, UPLOAD_RETRIES } from './s3Config';

/**
 * Gets the authentication token for API requests
 * @returns {String} - JWT token
 */
const getAuthToken = () => {
  // When used in components, you can use useSelector to get the user and admin state
  // For non-component contexts, check local storage directly
  const authToken = localStorage.getItem('authToken');
  if (authToken) {
    return authToken;
  }
  
  // If no direct token is found, we might be in a thunk where we'll pass the token directly
  log.warn('No auth token found in localStorage, API calls may fail');
  return null;
};

/**
 * Fetch with timeout and retry capability
 * @param {String} url - URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/**
 * Retry a function multiple times before giving up
 * @param {Function} fn - Function to retry
 * @param {Number} retries - Number of retries
 * @returns {Promise} - Result of function
 */
const retry = async (fn, retries = UPLOAD_RETRIES) => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 1) throw error;
    log.warn(`Retrying after error: ${error.message}. Attempts left: ${retries - 1}`);
    return retry(fn, retries - 1);
  }
};

/**
 * Upload files to S3 via backend API
 * @param {Array} filesToUpload - Array of files to upload
 * @param {String} bucket - Bucket type identifier (feedback, csv, viewer-settings, ghost-model)
 * @param {String} path - Path within bucket
 * @param {String} token - Optional JWT token (for use in thunks)
 * @returns {Array} - Array of S3 file URLs
 */
export const s3ApiUploader = async (filesToUpload, bucket, path, token = null) => {
  const s3Links = [];
  // Use provided token or get it from storage
  const authToken = token || getAuthToken();
  
  if (!authToken) {
    throw new Error('Authentication token required for file upload');
  }
  
  // Skip if there are no files to upload
  if (!filesToUpload || filesToUpload.length === 0) {
    return s3Links;
  }
  
  // Filter out files that are already uploaded (URLs as strings)
  const filesToActuallyUpload = filesToUpload.filter(item => typeof item === 'object');
  const alreadyUploadedLinks = filesToUpload.filter(item => typeof item !== 'object');
  
  // Add already uploaded links to results
  alreadyUploadedLinks.forEach(link => s3Links.push(link));
  
  // If no files need uploading, return early
  if (filesToActuallyUpload.length === 0) {
    return s3Links;
  }
  
  // Get pre-signed URLs from the backend API
  const fileNames = filesToActuallyUpload.map(file => file.name);
  
  try {
    log.debug(`Getting presigned URLs for ${fileNames.length} files`);
    
    // Step 1: Get presigned URLs
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/upload/get-upload-urls`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        fileNames,
        bucket,
        path
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get upload URLs');
    }
    
    const data = await response.json();
    log.debug(`Received ${data.urls.length} presigned URLs`);
    
    // Step 2: Upload files using the presigned URLs
    await Promise.all(
      filesToActuallyUpload.map(async (file, index) => {
        const uploadUrl = data.urls[index];
        
        await retry(async () => {
          log.debug(`Uploading file: ${file.name}`);
          const uploadResponse = await fetchWithTimeout(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              ...(file.type === 'image/svg+xml' && { 'Content-Type': 'image/svg+xml' })
            }
          });
          
          if (!uploadResponse.ok) {
            throw new Error(`Failed to upload file ${file.name}`);
          }
          
          log.debug(`Successfully uploaded: ${file.name}`);
        });
        
        s3Links.push(data.fileLinks[index]);
      })
    );
    
    return s3Links;
  } catch (error) {
    log.error('Error uploading files:', error);
    throw error;
  }
};

/**
 * Copy files between S3 directories via backend API
 * @param {String} ogDir - Original directory
 * @param {String} newDir - Destination directory
 * @param {String} bucket - Bucket type identifier
 * @param {Boolean} deleteOnCopy - Whether to delete original files
 * @param {String} token - Optional JWT token (for use in thunks)
 * @returns {Boolean} - Operation success
 */
export const s3ApiCopier = async (ogDir, newDir, bucket, deleteOnCopy = false, token = null) => {
  // Use provided token or get it from storage
  const authToken = token || getAuthToken();
  
  if (!authToken) {
    throw new Error('Authentication token required for file operations');
  }
  
  try {
    log.debug(`Copying files from ${ogDir} to ${newDir} in bucket ${bucket}`);
    
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/upload/copy-files`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        sourceDirectory: ogDir,
        destinationDirectory: newDir,
        bucket,
        deleteOriginals: deleteOnCopy
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to copy files');
    }
    
    const data = await response.json();
    log.debug(`Copy operation complete. Success: ${data.success}`);
    return data.success;
  } catch (error) {
    log.error('Error copying files:', error);
    return false;
  }
};